import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AgentLayout from './AgentLayout';
import { Loader, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/agent/commission`;
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export default function AgentCommission() {
  const [summary, setSummary] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([
          axios.get(`${API}/summary`, { withCredentials: true }),
          axios.get(`${API}/earnings`, { withCredentials: true, params: { limit: 50 } }),
        ]);
        setSummary(s.data);
        setEarnings(e.data.items || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const mtd = summary?.mtd || {};

  return (
    <AgentLayout>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commission Earnings</h1>
          <p className="text-sm text-gray-500">Your airline commission share (net after TDS)</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'MTD gross', value: fmt(mtd.gross) },
                { label: 'MTD TDS', value: fmt(mtd.tds) },
                { label: 'MTD net', value: fmt(mtd.net) },
                { label: 'Credited', value: fmt(mtd.credited) },
              ].map((c) => (
                <div key={c.label} className="bg-white border border-gray-200 rounded-xl p-4">
                  <p className="text-xs text-gray-500">{c.label}</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">{c.value}</p>
                </div>
              ))}
            </div>

            {mtd.pending > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                Pending payout: <strong>{fmt(mtd.pending)}</strong>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#1B375B]" />
                <h2 className="font-semibold text-gray-900">Recent earnings</h2>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Booking</th>
                    <th className="px-4 py-2">PNR</th>
                    <th className="px-4 py-2">Gross</th>
                    <th className="px-4 py-2">TDS</th>
                    <th className="px-4 py-2">Net</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {earnings.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No commission earnings yet</td></tr>
                  )}
                  {earnings.map((row) => (
                    <tr key={row.booking_id} className="border-t border-gray-100">
                      <td className="px-4 py-2 font-mono text-xs">{row.booking_id}</td>
                      <td className="px-4 py-2">{row.pnr || '—'}</td>
                      <td className="px-4 py-2">{fmt(row.gross)}</td>
                      <td className="px-4 py-2">{fmt(row.tds)}</td>
                      <td className="px-4 py-2 font-semibold">{fmt(row.net)}</td>
                      <td className="px-4 py-2 uppercase text-xs">{row.payout_status || 'pending'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AgentLayout>
  );
}
